export interface ImageTransformOptions {
  width?: number;
  height?: number;

  /**
   * `fit` (default): Scales the asset up or down so that it takes up as much space as possible within a bounding box defined by the specified dimension parameters without cropping any of it. The original aspect ratio is retained and all of the original image is visible.
   *
   * `pad`: Resizes the asset to fill the specified width and height while retaining the original aspect ratio and with all of the original asset visible. If the proportions of the original asset do not match the specified width and height, padding is added to the asset to reach the required size.
   *
   * Documentation: https://cloudinary.com/documentation/transformation_reference#c_crop_resize
   */
  crop?: 'fit' | 'pad';

  devicePixelRatio?: number;

  /** Default: jpg */
  format?: 'png' | 'jpg' | 'auto';
}

const cloudinaryDomain = process.env.REACT_APP_CLOUDINARY_DOMAIN;
const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const s3BucketName = process.env.REACT_APP_S3_BUCKET;
const awsRegion = process.env.REACT_APP_AWS_REGION;
const s3UrlPrefix = `https://${s3BucketName}.s3.${awsRegion}.amazonaws.com/images/`;

const getS3Url = (storageKey: string) => `${s3UrlPrefix}${storageKey}`;

/**
 * If we decide to change our s3 bucket or aws region for uploaded images,
 * we need to replace this check with an array of all internal urls
 */
export const isInternalImageUrl = (url: string) => url.startsWith(s3UrlPrefix);

export const getImageUrl = (storageKey: string, options?: ImageTransformOptions): string => {
  const { width, height, crop, devicePixelRatio, format } = options ?? {};

  const transformations: string[] = [`f_${format ?? 'jpg'}`];

  if (width !== undefined) {
    transformations.push(`w_${width}`);
  }
  if (height !== undefined) {
    transformations.push(`h_${height}`);
  }
  if (crop === 'pad') {
    transformations.push('c_pad');
  } else {
    transformations.push('c_fit');
  }
  if (devicePixelRatio) {
    transformations.push(`dpr_${devicePixelRatio}`);
  }

  // We do not use Cloudinary in Cypress tests because it consumes Cloudinary credits
  // and it's not critical to have the resizing functionality in those tests.
  if (process.env.REACT_APP_CYPRESS === 'true') {
    return getS3Url(storageKey);
  }

  return `https://${cloudinaryDomain}/${cloudName}/image/fetch/${transformations.join()}/${getS3Url(storageKey)}`;
};

export const getImageSrcSet = (storageKey: string, options: ImageTransformOptions): string => `
  ${getImageUrl(storageKey, { ...options, devicePixelRatio: 1 })} 1x,
  ${getImageUrl(storageKey, { ...options, devicePixelRatio: 2 })} 2x
`;
